// Admin - Cadastro Manual de Participantes
import logger from '../../lib/logger';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Check, AlertCircle, Mail, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CadastroManualPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [formData, setFormData] = useState({
    evento_id: '',
    lote_id: '',
    nome_responsavel: '',
    nome_conjuge: '',
    email: '',
    telefone: '',
    cidade: '',
    cpf: '',
    data_nascimento: '',
    observacoes_internas: '',
    valor_pago: 3000,
    forma_pagamento: 'pix_manual',
    status_pagamento: 'aprovado',
    responsavel_comercial: '',
    enviar_email_confirmacao: false
  });

  useEffect(() => {
    fetchEventos();
    fetchLotes();
  }, []);

  const fetchEventos = async () => {
    try {
      const response = await api.get('/eventos');
      setEventos(response.data || []);
      // Set default evento if available
      if (response.data && response.data.length > 0) {
        const eventoAtivo = response.data.find(e => e.status === 'ativo');
        if (eventoAtivo) {
          setFormData(prev => ({ ...prev, evento_id: eventoAtivo.evento_id || eventoAtivo.id }));
        }
      }
    } catch (error) {
      logger.error('Error fetching eventos:', error);
    }
  };

  const fetchLotes = async () => {
    try {
      const response = await api.get('/lotes');
      setLotes(response.data || []);
      // Set default lote if available
      if (response.data && response.data.length > 0) {
        const loteAtivo = response.data.find(l => l.ativo || l.status === 'ativo');
        if (loteAtivo) {
          setFormData(prev => ({ 
            ...prev, 
            lote_id: loteAtivo.lote_id || loteAtivo.id,
            valor_pago: loteAtivo.valor || loteAtivo.preco || 3000
          }));
        }
      }
    } catch (error) {
      logger.error('Error fetching lotes:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/participantes/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          valor_pago: parseFloat(formData.valor_pago) || 0
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Participante cadastrado com sucesso!');
        if (data.email_enviado) {
          toast.success('E-mail de confirmação enviado!');
        }
        navigate('/admin/participantes');
      } else {
        toast.error(data.detail || 'Erro ao cadastrar participante');
      }
    } catch (error) {
      logger.error('Error:', error);
      toast.error('Erro ao cadastrar participante');
    } finally {
      setLoading(false);
    }
  };

  const formasPagamento = [
    { value: 'pix_manual', label: 'PIX Manual' },
    { value: 'dinheiro', label: 'Dinheiro' },
    { value: 'transferencia', label: 'Transferência Bancária' },
    { value: 'cartao_externo', label: 'Cartão (Externo)' },
    { value: 'cortesia', label: 'Cortesia' },
    { value: 'outro', label: 'Outro' }
  ];

  const statusPagamento = [
    { value: 'aprovado', label: 'Aprovado', color: 'text-green-500' },
    { value: 'cortesia', label: 'Cortesia', color: 'text-purple-500' },
    { value: 'pendente', label: 'Pendente', color: 'text-yellow-500' },
    { value: 'cancelado', label: 'Cancelado', color: 'text-red-500' }
  ];

  return (
    <div className="min-h-screen bg-obsidian p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/participantes">
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-kairos-orange" />
              Cadastro Manual de Participante
            </h1>
            <p className="text-gray-400 text-sm">Adicionar casal manualmente ao sistema</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Dados do Casal */}
          <Card className="bg-obsidian-50 border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Dados do Casal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Nome do Responsável *</Label>
                  <Input
                    name="nome_responsavel"
                    value={formData.nome_responsavel}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Nome do Cônjuge *</Label>
                  <Input
                    name="nome_conjuge"
                    value={formData.nome_conjuge}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="Nome completo"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">E-mail *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Telefone *</Label>
                  <Input
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Cidade *</Label>
                  <Input
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="Cidade/UF"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">CPF</Label>
                  <Input
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleChange}
                    className="bg-obsidian border-white/20 text-white"
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Data de Nascimento</Label>
                  <Input
                    name="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={handleChange}
                    className="bg-obsidian border-white/20 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dados do Evento e Pagamento */}
          <Card className="bg-obsidian-50 border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Evento e Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Evento *</Label>
                  <select
                    name="evento_id"
                    value={formData.evento_id}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md bg-obsidian border border-white/20 text-white"
                  >
                    <option value="">Selecione o evento</option>
                    {eventos.map(e => (
                      <option key={e.evento_id || e.id} value={e.evento_id || e.id}>
                        {e.nome || e.nome_evento}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Lote *</Label>
                  <select
                    name="lote_id"
                    value={formData.lote_id}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md bg-obsidian border border-white/20 text-white"
                  >
                    <option value="">Selecione o lote</option>
                    {lotes.map(l => (
                      <option key={l.lote_id || l.id} value={l.lote_id || l.id}>
                        {l.nome_lote || l.nome} - R$ {(l.valor || l.preco || 0).toLocaleString('pt-BR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Valor Pago (R$) *</Label>
                  <Input
                    name="valor_pago"
                    type="number"
                    step="0.01"
                    value={formData.valor_pago}
                    onChange={handleChange}
                    required
                    className="bg-obsidian border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Forma de Pagamento *</Label>
                  <select
                    name="forma_pagamento"
                    value={formData.forma_pagamento}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md bg-obsidian border border-white/20 text-white"
                  >
                    {formasPagamento.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Status do Pagamento *</Label>
                  <select
                    name="status_pagamento"
                    value={formData.status_pagamento}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md bg-obsidian border border-white/20 text-white"
                  >
                    {statusPagamento.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Responsável Comercial / Enviado por</Label>
                <Input
                  name="responsavel_comercial"
                  value={formData.responsavel_comercial}
                  onChange={handleChange}
                  className="bg-obsidian border-white/20 text-white"
                  placeholder="Nome do vendedor ou indicação"
                />
              </div>
            </CardContent>
          </Card>

          {/* Observações e Opções */}
          <Card className="bg-obsidian-50 border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Observações e Opções</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Observações Internas</Label>
                <textarea
                  name="observacoes_internas"
                  value={formData.observacoes_internas}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md bg-obsidian border border-white/20 text-white resize-none"
                  placeholder="Notas internas (não visíveis para o participante)"
                />
              </div>

              {/* Email checkbox */}
              {(formData.status_pagamento === 'aprovado' || formData.status_pagamento === 'cortesia') && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-kairos-orange/10 border border-kairos-orange/30">
                  <input
                    type="checkbox"
                    id="enviar_email"
                    name="enviar_email_confirmacao"
                    checked={formData.enviar_email_confirmacao}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-kairos-orange text-kairos-orange focus:ring-kairos-orange"
                  />
                  <label htmlFor="enviar_email" className="text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-kairos-orange" />
                    Enviar e-mail de confirmação com QR Code agora
                  </label>
                </div>
              )}

              {/* Info boxes */}
              {formData.status_pagamento === 'aprovado' || formData.status_pagamento === 'cortesia' ? (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-green-400 font-medium">Inscrição será confirmada</p>
                    <p className="text-green-300/70 text-sm">
                      O participante ocupará uma vaga, receberá código de ingresso e QR Code.
                    </p>
                  </div>
                </div>
              ) : formData.status_pagamento === 'pendente' ? (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 font-medium">Aguardando confirmação</p>
                    <p className="text-yellow-300/70 text-sm">
                      O participante ficará pendente até a confirmação do pagamento.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Inscrição cancelada</p>
                    <p className="text-red-300/70 text-sm">
                      O participante não ocupará vaga e não receberá ingresso.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Link to="/admin/participantes">
              <Button type="button" variant="outline" className="border-white/20 text-gray-300">
                Cancelar
              </Button>
            </Link>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-kairos-orange hover:bg-kairos-orange/90 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Cadastrar Participante
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroManualPage;
